// Centralized client-side upload size limits and messaging.
//
// The backend/proxy rejects oversized uploads with HTTP 413 (handled as a
// safety net in lib/api-helpers.ts). These client-side guards stop the upload
// *before* the network round-trip and tell the user the exact limit, so the
// 413 path is only ever hit when the server limit differs or a guard is missed.

import { toast } from "@/lib/toast";

/**
 * Max upload size (MB) for general attachments: comment files, form-question
 * files, file-property cells, cover images, avatars, marketplace images.
 */
export const MAX_UPLOAD_SIZE_MB = 15;

/** Max upload size (MB) for images embedded inline in the editor. */
export const MAX_EDITOR_IMAGE_SIZE_MB = 20;

/**
 * Max size (in characters) of a single paste before we divert it to "import as
 * a page" instead of pasting inline. Pasting very large HTML/text creates one
 * synced block per node, which janks the editor. Measured against the raw
 * clipboard string (HTML or plain text, whichever is larger) — NOT bytes, so
 * it's a fast synchronous check inside the paste handler.
 *
 * ~100k chars ≈ a large document; tune against real "slow paste" samples.
 */
export const MAX_PASTE_CONTENT_CHARS = 100_000;

/**
 * User-facing message for an oversized file. Pass the applicable limit to name
 * it ("smaller than 15 MB"); omit it for a generic message (e.g. the 413 safety
 * net, where the exact limit isn't known client-side).
 */
export const fileTooLargeMessage = (maxSizeMB?: number): string =>
  typeof maxSizeMB === "number"
    ? `File is too large. Please upload a file smaller than ${maxSizeMB} MB.`
    : "File is too large. Please upload a smaller file.";

/**
 * Returns true — and shows an error toast — when `file` exceeds `maxSizeMB`.
 * Use as an early guard before uploading: `if (isFileTooLarge(file)) return;`
 */
export const isFileTooLarge = (file: File, maxSizeMB: number = MAX_UPLOAD_SIZE_MB): boolean => {
  if (file.size / 1024 / 1024 > maxSizeMB) {
    toast.error(fileTooLargeMessage(maxSizeMB));
    return true;
  }
  return false;
};
